import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  Pagination,
  Menu,
  MenuItem,
  useMediaQuery,
} from '@mui/material'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import FilterAltIcon from '@mui/icons-material/FilterAlt'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import back from '../../../../assets/back.svg'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { decryptToken } from '../../../../Utlis/Encryption'
import { BASE_URL } from '../../../../components/BaseUrl'
import CustomText, { textStyles } from '../../../../components/CustomText'
import SearchInput from '../../../../components/SearchInput'
import WorkerStatusDropdown from '../../../../components/workerUpdateStatus'
import FilterDrawer from './FilterDrawer'
import WorkerDetails from '../AllWorkers/WorkerDetails'
import DeleteWorkerModal from '../AllWorkers/DeleteModal'
import { toast } from 'react-hot-toast'
import AddScheduleDrawer from './AddScheduleDrawer'
import DepartmentDetails from './DepartmentDetails'
import AddIcon from "@mui/icons-material/Add";
import { useTheme } from "../../../../contexts/ThemeContext";
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
const DepartmentDetail = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const [workerRefreshKey, setWorkerRefreshKey] = useState(0);

  const { id } = useParams()
  const { t } = useTranslation()
  const { primaryColor, secondaryColor, primaryTextColor, secondaryTextColor } = useTheme();
  const isMobile = useMediaQuery('(max-width:900px)');

  // Get department data from navigation state
  const department = location.state?.department
  
  // Auth
  const token = useSelector((state) => state.auth.token)
  const decryptedToken = token ? decryptToken(token) : null
  
  // State for workers table
  const [workers, setWorkers] = useState([])
  const [initialLoading, setInitialLoading] = useState(true)
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [viewScheduleOpen, setViewScheduleOpen] = useState(false);
  const [tableLoading, setTableLoading] = useState(false)
  const [errorMsg, setErrorMsg] = useState('')
  const [page, setPage] = useState(0)
  const [pageSize] = useState(5)
  const [totalPages, setTotalPages] = useState(1)
  
  // Department data from API
  const [departmentData, setDepartmentData] = useState(null)
  
  // Sorting
  const [sortBy, setSortBy] = useState('created_at')
  const [sortOrder, setSortOrder] = useState('desc')
  
  // Search and filters
  const [searchTerm, setSearchTerm] = useState('')
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false)
  const [filters, setFilters] = useState({ 
    status: '', 
    startDate: '', 
    endDate: '' 
  })
  const isFilterApplied = Boolean(filters.status || filters.startDate || filters.endDate)
  
  // Menu state
  const [menuAnchor, setMenuAnchor] = useState(null)
  const [menuWorker, setMenuWorker] = useState(null)
  
  // Worker details and delete modal state
  const [selectedWorker, setSelectedWorker] = useState(null)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [deleteLoading, setDeleteLoading] = useState(false)
  const [deleteWorker, setDeleteWorker] = useState(null)
  
  const firstLoadRef = useRef(true)

  const handleBack = () => {
    navigate('/department')
  }

  // Fetch workers for this department
  const fetchDepartmentWorkers = useCallback(
    async ({ initial = false } = {}) => {
      try {
        if (initial) setInitialLoading(true)
        else setTableLoading(true)

        setErrorMsg('')

        const params = new URLSearchParams({
          page: String(page + 1),
          limit: String(pageSize),
          sort_by: sortBy,
          sort_order: sortOrder,
        })

        if (searchTerm) params.set('search', searchTerm)
        if (filters.status) params.set('status', filters.status)
        if (filters.startDate) params.set('startDate', filters.startDate)
        if (filters.endDate) params.set('endDate', filters.endDate)

        const url = `${BASE_URL}/departments/${id}?${params.toString()}`
        console.log('📡 Department Workers API URL:', url)
        console.log('📄 Current page:', page + 1, 'Page size:', pageSize)

        const res = await fetch(url, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${decryptedToken}`,
            'Content-Type': 'application/json',
          },
        })

        const json = await res.json()
        console.log('📊 Department Workers API Response:', json)
        console.log('📊 Workers count:', json?.data?.department?.workers?.length)
        console.log('📊 Total pages from API:', json?.data?.pagination?.pages)

        if (json?.error) {
          setErrorMsg(json?.message || 'Failed to fetch department workers.')
          setWorkers([])
          setTotalPages(1)
          return
        }

        if (Array.isArray(json?.data?.department?.workers)) {
          const mapped = json.data.department.workers.map((w) => ({
            id: w.id,
            name: `${w.first_name} ${w.last_name}`,
            email: w.email,
            phone: w.phone,
            designation: w.designation || '-',
            type: w.employee_type || '-',
            hireDate: w.hire_date,
            status: w.status || '-',
            profileImage: w.profile_image,
            date: w.created_at
              ? new Date(w.created_at).toLocaleString('en-GB', {
                  day: '2-digit',
                  month: 'short',
                  year: 'numeric',
                })
              : '—',
          }))
          setWorkers(mapped)
          
          // Store department data from API
          setDepartmentData(json.data.department)
          
          // Use the pagination info from the main data object
          const pagination = json.data.pagination
          console.log('📊 Pagination info:', pagination)
          setTotalPages(pagination?.pages || 1)
        } else {
          setWorkers([])
          setTotalPages(1)
        }
      } catch (err) {
        console.error('❌ Error fetching department workers:', err)
        setErrorMsg('Network error while fetching department workers.')
        setWorkers([])
        setTotalPages(1)
      } finally {
        if (initial) setInitialLoading(false)
        else setTableLoading(false)
      }
    },
    [decryptedToken, id, page, pageSize, searchTerm, sortBy, sortOrder, filters.status, filters.startDate, filters.endDate]
  )

  // Effect to fetch workers
  useEffect(() => {
    if (!decryptedToken || !id) {
      setInitialLoading(false)
      setErrorMsg('Missing auth token or department ID.')
      return
    }

    const isInitial = firstLoadRef.current
    fetchDepartmentWorkers({ initial: isInitial })
    if (isInitial) firstLoadRef.current = false
  }, [decryptedToken, id, page, pageSize, searchTerm, sortBy, sortOrder, filters.status, filters.startDate, filters.endDate, fetchDepartmentWorkers])

  // Menu handlers
  const handleMenuOpen = (event, worker) => {
    setMenuAnchor(event.currentTarget)
    setMenuWorker(worker)
  }

  const handleMenuClose = () => {
    setMenuAnchor(null)
    setMenuWorker(null)
  }

  const handleView = () => {
    if (menuWorker) setSelectedWorker(menuWorker)
    handleMenuClose()
  }

  const handleDeleteClick = () => {
    setDeleteWorker(menuWorker)
    setDeleteOpen(true)
    handleMenuClose()
  }

  // Status update handler
  const handleStatusUpdate = async (workerId, newStatus) => {
    try {
      const res = await fetch(`${BASE_URL}/company-admins/workers/${workerId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${decryptedToken}`,
        },
        body: JSON.stringify({ status: newStatus }),
      })

      const json = await res.json()

      if (res.ok && !json.error) {
        setWorkers((prev) =>
          prev.map((w) =>
            w.id === workerId ? { ...w, status: newStatus } : w
          )
        )
        toast.success('Status updated successfully')
      } else {
        toast.error(json.message || 'Failed to update status')
      }
    } catch (err) {
      console.error('Error updating worker status:', err)
      toast.error('Network error while updating status')
    }
  }

  // Sorting handler
  const handleSort = (column) => {
    let nextOrder = 'asc'
    if (sortBy === column) {
      nextOrder = sortOrder === 'asc' ? 'desc' : 'asc'
    }
    setSortBy(column)
    setSortOrder(nextOrder)
  }

  const isEmpty = !initialLoading && !tableLoading && !errorMsg && workers.length === 0

  // Delete worker API
  const handleDeleteWorker = async () => {
    if (!deleteWorker) return
    setDeleteLoading(true)

    try {
      const res = await fetch(`${BASE_URL}/company-admins/admin/workers/${deleteWorker.id}/delete`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${decryptedToken}`,
        },
      })

      const json = await res.json()

      if (res.ok && !json.error) {
        toast.success('Worker deleted successfully')
        setWorkers((prev) => prev.filter((w) => w.id !== deleteWorker.id))
        setDeleteOpen(false)
      } else {
        toast.error(json.message || 'Failed to delete worker')
      }
    } catch (err) {
      console.error('Error deleting worker:', err)
      toast.error('Network error while deleting worker')
    } finally {
      setDeleteLoading(false)
    }
  }

  // Styles
  const headerStyles = {
    color: '#44546F',
    fontSize: '14px',
    fontFamily: 'Poppins, sans-serif',
    fontWeight: 600,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    cursor: 'pointer',
  }

  const dataStyles = {
    color: '#172B4D',
    fontSize: '12px',
    fontFamily: 'Poppins, sans-serif',
    fontWeight: 400,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  }

  if (!department && !departmentData) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" color="error">
        {t("noDepartmentData")}
        </Typography>
        <Button onClick={handleBack} sx={{ mt: 2 }}>
          {t("goBack")}
        </Button>
      </Box>
    )
  }

  // Use API data if available, otherwise fall back to navigation state
  const currentDepartment = departmentData || department

  return (
    <>
      {/* Top Back Bar */}
      <Box
        sx={{
          border: "1px solid #E4E6EF",
          borderRadius: 2,
          px: 2,
          backgroundColor: "white",
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          minHeight: 55,
          ml: 1,
          mr: 1,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            cursor: "pointer",
          }}
          onClick={handleBack}
        >
          <img src={back} style={{ width: 30, height: 30 }} alt="back" />
          <CustomText
            sx={{
              ...textStyles.h1,
              fontWeight: 500,
              fontSize: "14px",
              color: "#626F86",
            }}
          >
            {selectedWorker ? `Department / ${currentDepartment.name} / ${selectedWorker.name}` : `Department / ${currentDepartment.name}`}
          </CustomText>
        </Box>
      </Box>

      {/* Main Layout */}
      <Box
        sx={{
          backgroundColor: '#F8F9FA',
          borderRadius: 2,
          py: 2,
          ml: 1,
          mr: 1,
          mt: 1,
          border: '2px solid #091E4224',
          minHeight: '400px',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
      {/* Search and Filter Controls */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'nowrap',
          pb: 2,
          px: 2,
          pt: 1,
          gap: isMobile ? 1 : 2,
        }}
      >
        <Typography
          sx={{
            fontSize: isMobile ? '14px' : '18px',
            fontWeight: 600,
            color: '#003149',
            fontFamily: 'Poppins, sans-serif',
            minWidth: 'fit-content',
            flexShrink: 0,
          }}
        >
          {t('departments.employeesDetail')}
        </Typography>
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <SearchInput
            placeholder={t('workers.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            minWidth={isMobile ? 80 : 250}
            maxWidth={isMobile ? 120 : 300}
          />
        </Box>

        <Box
          sx={{
            flexShrink: 0,
            display: 'flex',
            flexWrap: 'nowrap',
            justifyContent: 'flex-end',
            gap: isMobile ? 0.5 : 1,
          }}
        >
        <Button
                      onClick={() => setViewScheduleOpen(true)}
                      variant="outlined"
                      size="small"
                      sx={{
                        px: isMobile ? 1 : 2,
                        minWidth: 0,
                        height: isMobile ? 26 : 32,
                        fontSize: isMobile ? 11 : 14,
                        fontWeight: 400,
                        fontFamily: "Poppins, sans-serif",
                        borderRadius: "4px",
                        textTransform: "none",
                        whiteSpace: "nowrap",
                        flexShrink: 0,
                        color: primaryColor,
                        borderColor: primaryColor,
                        "& .MuiButton-startIcon": { mr: isMobile ? 0.3 : 0.7 },
                        "&:hover": {
                          borderColor: secondaryColor,
                          color: secondaryColor,
                        },
                      }}
                      startIcon={<VisibilityIcon sx={{ fontSize: isMobile ? 14 : 18 }} />}
                    >
                      {t("departmentDetails.viewSchedule", "View Schedule")}
                    </Button>
        <Button
                      onClick={() => setDrawerOpen(true)}
                      variant="contained"
                      size="small"
                      sx={{
                        px: isMobile ? 1 : 2,
                        minWidth: 0,
                        backgroundColor: primaryColor,
                        color: primaryTextColor,
                        height: isMobile ? 26 : 32,
                        fontSize: isMobile ? 11 : 14,
                        fontWeight: 400,
                        fontFamily: "Poppins, sans-serif",
                        borderRadius: "4px",
                        textTransform: "none",
                        whiteSpace: "nowrap",
                        flexShrink: 0,
                        transition: "all 0.3s ease",
                        "& .MuiButton-startIcon": { mr: isMobile ? 0.3 : 0.7 },
                        "&:hover": {
                          backgroundColor: secondaryColor,
                          color: secondaryTextColor,
                        },
                      }}
                      startIcon={<EditIcon sx={{ fontSize: isMobile ? 14 : 18 }} />}
                    >
                      {t("departmentSchedule")}
                    </Button>
          {isFilterApplied ? (
            <Box
              sx={{
                position: 'relative',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: isMobile ? 26 : 34,
                height: isMobile ? 26 : 34,
                flexShrink: 0,
                borderRadius: 1,
                bgcolor: '#006EC2',
              }}
            >
              <FilterAltIcon sx={{ color: 'white', fontSize: isMobile ? 15 : 20 }} />
              <IconButton
                size="small"
                onClick={() => {
                  setFilters({ status: '', startDate: '', endDate: '' })
                  setPage(0)
                }}
                sx={{
                  position: 'absolute',
                  top: -6,
                  right: -6,
                  bgcolor: 'white',
                  width: 18,
                  height: 18,
                  border: '1px solid #C4C4C4',
                  '&:hover': { bgcolor: '#f5f5f5' },
                }}
              >
                <CloseRoundedIcon sx={{ fontSize: 14, color: '#333' }} />
              </IconButton>
            </Box>
          ) : (
            <IconButton
              onClick={() => setFilterDrawerOpen(true)}
              sx={{
                border: '1px solid #C4C4C4',
                borderRadius: 1,
                width: isMobile ? 26 : 34,
                height: isMobile ? 26 : 34,
                flexShrink: 0,
                p: isMobile ? 0.5 : 1,
              }}
            >
              <FilterAltIcon sx={{ fontSize: isMobile ? 16 : 24 }} />
            </IconButton>
          )}
        </Box>
      </Box>

      {/* Workers Table */}
        {initialLoading ? (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        ) : errorMsg ? (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
            <Typography color="error" sx={{ fontSize: 14 }}>
              {errorMsg}
            </Typography>
          </Box>
        ) : selectedWorker ? (
          <WorkerDetails worker={selectedWorker} onBack={() => setSelectedWorker(null)} 
             refreshKey={workerRefreshKey}
/>
        ) : isEmpty ? (
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 5 }}>
            <Typography variant="h6" color="text.secondary">
             {t("noWorkersFound")}
            </Typography>
          </Box>
        ) : (
          <>
            {/* Table */}
            <TableContainer component={Paper} sx={{ boxShadow: 'none', width: '100%', overflowX: 'auto' }}>
              <Table sx={{ minWidth: '700px' }}>
                <TableHead sx={{ borderTop: '2px solid #091E4224', borderBottom: '2px solid #091E4224' }}>
                  <TableRow>
                    <TableCell sx={{ ...headerStyles, pl: 1.5 }} onClick={() => handleSort('id')}>
                      {t('common.id')}
                    </TableCell>
                    <TableCell sx={headerStyles} onClick={() => handleSort('name')}>
                      {t('common.workerName')}
                    </TableCell>
                    <TableCell sx={headerStyles}>{t('common.email')}</TableCell>
                    <TableCell sx={headerStyles}>{t('common.phoneNo')}</TableCell>
                    <TableCell sx={headerStyles}>{t('common.designation')}</TableCell>
                    <TableCell sx={headerStyles}>{t('common.type')}</TableCell>
                    <TableCell sx={headerStyles}>{t('common.status')}</TableCell>
                    <TableCell sx={headerStyles} onClick={() => handleSort('created_at')}>
                      {t('common.date')}
                    </TableCell>
                    <TableCell sx={headerStyles}>{t('common.action')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {workers.map((worker) => (
                    <TableRow key={worker.id}>
                      <TableCell sx={{ ...dataStyles, pl: 1.5 }}>{worker.id}</TableCell>
                      <TableCell sx={dataStyles}>{worker.name}</TableCell>
                      <TableCell sx={dataStyles}>{worker.email}</TableCell>
                      <TableCell sx={dataStyles}>{worker.phone || '_'}</TableCell>
                      <TableCell sx={dataStyles}>{worker.designation}</TableCell>
                      <TableCell sx={dataStyles}>{worker.type}</TableCell>
                      <TableCell>
                        <Box sx={{ pointerEvents: "none" }}>
                        <WorkerStatusDropdown
                          currentStatus={worker.status}
                            showArrow={false}   // 👈 arrow hidden

                        />
                        </Box>
                      </TableCell>
                      <TableCell sx={dataStyles}>{worker.date}</TableCell>
                      <TableCell>
                        <IconButton size="small" onClick={(e) => handleMenuOpen(e, worker)}>
                          <MoreVertIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', mt: 2 }}>
              <Pagination
                count={totalPages}
                page={page + 1}
                onChange={(e, value) => setPage(value - 1)}
                shape="rounded"
                color="primary"
                size="small"
                sx={{
                  '& .MuiPaginationItem-root': {
                    fontFamily: 'Poppins, sans-serif',
                    fontSize: 13,
                    fontWeight: 500,
                  },
                  '& .Mui-selected': {
                    backgroundColor: '#006EC210',
                    color: '#006EC2',
                  },
                }}
              />
            </Box>
          </>
        )}

        {/* Row action menu */}
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={handleMenuClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <MenuItem onClick={handleView} sx={{ fontSize: 13, fontFamily: 'Poppins, sans-serif' }}>
            {t('common.view')}
          </MenuItem>
          <MenuItem onClick={handleDeleteClick} sx={{ fontSize: 13, fontFamily: 'Poppins, sans-serif' }}>
            {t('common.delete')}
          </MenuItem>
        </Menu>

      {/* Filter Drawer */}
      <FilterDrawer
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
        filters={filters}
        setFilters={setFilters}
        onApplyFilters={(nextFilters) => {
          setFilters(nextFilters)
          setPage(0)
        }}
      />

      {/* Delete Worker Modal */}
      <DeleteWorkerModal
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={handleDeleteWorker}
        loading={deleteLoading}
        worker={deleteWorker}
      />
    </Box>

     <AddScheduleDrawer
         open={drawerOpen}
         onClose={() => setDrawerOpen(false)}
         departmentId={id}
        //  onAddSuccess={() => fetchDepartmentWorkers()}
           onAddSuccess={() => {
    setWorkerRefreshKey(prev => prev + 1);  // 🔥 trigger refresh
  }}
       />

      <DepartmentDetails
        department={currentDepartment}
        open={viewScheduleOpen}
        onClose={() => setViewScheduleOpen(false)}
      />
    </>
  )
}

export default DepartmentDetail


